import Ajv, { type ErrorObject, type Options } from "ajv";
import addFormats from "ajv-formats";
import type { JSONSchema7Definition } from "json-schema";
import type { ConnectionProviderKey, FeatureFlagKey } from "./schemas";

// Available on Node 18+ and every modern browser, but absent from this package's ES2020 lib.
declare function structuredClone<T>(value: T): T;

export type JSONSchemaDefinition = JSONSchema7Definition & {
  isDynamic?: boolean;
  shouldReload?: boolean;
  allowsSearch?: boolean;
  isCronTimer?: boolean;
  isManualOnly?: boolean;
  featureFlag?: FeatureFlagKey;
  orgSetting?: string;
  enumNames?: string[];
  suffix?: string;
  appConnectionConfig?: {
    appId: string;
    providerId: ConnectionProviderKey;
  };
};

/**
 * Builds an Ajv instance with FP's custom formats and metadata keywords registered.
 * Validating a user-authored schema needs its own instance, since Ajv rejects
 * a second schema reusing an `$id` already compiled on the same instance.
 * @param options - Ajv options merged over the shared defaults
 * @returns The configured Ajv instance
 */
export function buildAjv(options: Options): Ajv {
  const ajvInstance = new Ajv({ allErrors: true, verbose: true, ...options });
  addFormats(ajvInstance);
  ajvInstance.addFormat("fpPhoneNumber", /.*/);
  ajvInstance.addFormat("fpEmailAddress", /.*/);
  ajvInstance.addFormat("fpAsset", /.*/);
  ajvInstance.addFormat("fpTextarea", /.*/);
  ajvInstance.addFormat("fpCompany", /.*/);
  ajvInstance.addFormat("fpUser", /.*/);
  ajvInstance.addFormat("fpItem", /.*/);
  ajvInstance.addFormat("fpOperation", /.*/);
  ajvInstance.addFormat("percentage", /.*/);
  ajvInstance.addFormat("select", /.*/);
  ajvInstance.addFormat("fpLocation", /.*/);
  ajvInstance.addFormat("fpOpportunity", /.*/);
  ajvInstance.addFormat("fpStageId", /.*/);
  ajvInstance.addFormat("fpCalculation", /.*/);
  ajvInstance.addKeyword("isDynamic");
  ajvInstance.addKeyword("shouldReload");
  ajvInstance.addKeyword("allowsSearch");
  ajvInstance.addKeyword("isCronTimer");
  ajvInstance.addKeyword("isManualOnly");
  ajvInstance.addKeyword("featureFlag");
  ajvInstance.addKeyword("orgSetting");
  ajvInstance.addKeyword("appConnectionConfig");
  ajvInstance.addKeyword("propertyOrder");
  ajvInstance.addKeyword("enumNames");
  ajvInstance.addKeyword("suffix");
  return ajvInstance;
}

/** Shared Ajv instance used for strict (non-coercing) JSON Schema validation. */
export const ajv = buildAjv({});

/**
 * Ajv instance that coerces mismatched value shapes (string<->number<->boolean
 * and scalar->array) during validation instead of rejecting them.
 */
const coercingAjv = buildAjv({ coerceTypes: "array" });

/** Outcome of coercing an object against a JSON Schema. */
export interface CoerceObjectResult<T> {
  value: T;
  errors: ErrorObject[];
}

/**
 * Coerces a copy of an object into the shapes its JSON Schema declares.
 * Arrays are collapsed to their first element for non-array properties (the one
 * mismatch ajv cannot coerce for nullable union types such as ["string","null"]),
 * then ajv coerces the rest (string<->number<->boolean, scalar->array).
 * @param value - The object to coerce; left unmodified
 * @param schema - The JSON Schema to coerce against
 * @returns The coerced copy and any validation errors that remain
 */
export function coerceObject<T extends Record<string, unknown>>(
  value: T,
  schema: JSONSchemaDefinition,
): CoerceObjectResult<T> {
  const coerced = structuredClone(value);
  collapseArraysForScalarProperties(coerced, schema);
  const validate = coercingAjv.compile(schema);
  const valid = validate(coerced);
  return { value: coerced, errors: valid ? [] : (validate.errors ?? []) };
}

function collapseArraysForScalarProperties(
  values: Record<string, unknown>,
  schema: JSONSchemaDefinition,
): void {
  if (typeof schema === "boolean" || !schema.properties) {
    return;
  }
  for (const [key, value] of Object.entries(values)) {
    const property = schema.properties[key];
    if (!property || !Array.isArray(value) || isArrayProperty(property)) {
      continue;
    }
    values[key] = value[0] ?? null;
  }
}

function isArrayProperty(property: JSONSchema7Definition): boolean {
  if (typeof property === "boolean") {
    return false;
  }
  return Array.isArray(property.type)
    ? property.type.includes("array")
    : property.type === "array";
}
